import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { 
  Hotel, Check, ArrowRight, Star, Users, CreditCard,
  Clock, Sparkles
} from 'lucide-react';

const HospitalityPageEN = () => {
  return (
    <div data-testid="hospitality-page-en" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
              <Hotel className="w-4 h-4 text-slate-900" />
              <span className="text-sm font-semibold text-slate-900">Hotels & Tourism</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-6">
              Premium Service for Your Guests
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              EV charging as a premium amenity for your guests. 
              Attract eco-conscious travelers and create additional revenue streams.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/en/contact">
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
                Why EV Charging for Hotels?
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Modern travelers increasingly choose accommodations that offer EV charging. 
                Make your hotel their preferred choice.
              </p>
              
              <ul className="space-y-4">
                {[
                  'Attract a growing segment of EV drivers',
                  'Increase guest satisfaction and loyalty',
                  'Create additional revenue opportunities',
                  'Boost your sustainability credentials',
                  'Stand out from competition'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-slate-900" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                alt="Luxury hotel"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white rounded-2xl p-5 shadow-lg">
                <div className="flex items-center gap-3">
                  <Star className="w-8 h-8 text-yellow-400" />
                  <div>
                    <p className="text-2xl font-bold font-['Inter_Tight']">5-Star</p>
                    <p className="text-blue-200 text-sm">Service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              What We Offer
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Sparkles, title: 'Custom Branding', desc: 'Chargers with your hotel branding' },
              { icon: Users, title: 'Guest Integration', desc: 'Seamless billing through room charge' },
              { icon: Clock, title: 'Valet Charging', desc: 'Charge while guests enjoy amenities' },
              { icon: CreditCard, title: 'Revenue Share', desc: 'Earn from each charging session' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200/50">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonial */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Star className="w-12 h-12 text-yellow-500 mx-auto mb-6" />
            <blockquote className="text-2xl text-slate-700 italic mb-6">
              "Installing AlbMove chargers was one of our best decisions. Our guests specifically mention 
              EV charging in their reviews, and we've seen an increase in bookings from eco-conscious travelers."
            </blockquote>
            <p className="text-slate-900 font-semibold">— Hotel Manager, Durrës</p>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Inter_Tight'] mb-4">
              Elevate Your Guest Experience
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Contact us to learn how EV charging can enhance your hospitality offering.
            </p>
            <Button 
              asChild
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/en/contact">
                Contact Us Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HospitalityPageEN;
